import { useEffect } from "react";
import { useLoginContext } from "../../../App";
import { useContentContext } from "../../layout/Layout";
import { plansRelationToFws } from "../../../services/b2b-frameworks-apiService";
import { showNotification } from "../../b2b-common/Notification";
import { useQuery } from '@tanstack/react-query';
import useB2BTranslation from "../../b2b-common/hooks/useB2BTranslation";

// If filterFws is null, it will return all the frameworks related to the plan
// If filterFws is an array of objects, it will only retrieve from the frameworks included in the filterFws array
// Example: filterFws = [{frameworkSuite: 'generic', fwIds: [1, 2]}] will only retrieve the specified generic frameworks related to the plan.

const fetchFrameworks = async (type, planUUID, accessToken) => {
    const response = await plansRelationToFws(type, [planUUID], true, accessToken);
    return response;
};

const usePlansFrameworks = (planUUID, filterFws = null) => {

    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-plans');

    const { accessToken, productAcronyms } = loginData;

    const fetchAndSetFrameworks = async (type, acronym) => {
        const filter = filterFws?.find(f => f.frameworkSuite === type);
        if (filterFws === null || filter) {
            if (productAcronyms.includes(acronym)) {
                const result = await fetchFrameworks(type, planUUID, accessToken);
                // Sort level1sDTOs and level2sDTOs by their levelN_ItemId
                result.forEach(fw => {
                    fw.level1sDTOs.sort((a, b) => a.levelN_ItemId - b.levelN_ItemId);
                });
                // Filter the result if filter.fwIds is provided
                let filteredResult = result;
                if (filter?.fwIds?.length) {
                    filteredResult = result.filter(fw => filter.fwIds.includes(fw.frameworkDTO.frameworkId));
                }
                // Return the sorted and filtered result
                return filteredResult;
            }
        }
        return [];
    };
    
    const { data: genericFwsRelated, error: genericError } = useQuery({
        queryKey: ['genericFwsRelated', planUUID],
        queryFn: () => fetchAndSetFrameworks('generic', 'B2B_BASE'),
        enabled: !!planUUID,
    });

    const { data: iotFwsRelated, error: iotError } = useQuery({
        queryKey: ['iotFwsRelated', planUUID],
        queryFn: () => fetchAndSetFrameworks('iot', 'B2B_IOT_FW'),
        enabled: !!planUUID,
    });

    const { data: verticalFwsRelated, error: verticalError } = useQuery({
        queryKey: ['verticalFwsRelated', planUUID],
        queryFn: () => fetchAndSetFrameworks('vertical', 'B2B_VERTICALFW'),
        enabled: !!planUUID,
    });

    const { data: softwareFwsRelated, error: softwareError } = useQuery({
        queryKey: ['softwareFwsRelated', planUUID],
        queryFn: () => fetchAndSetFrameworks('software', 'B2B_SOFTWAREFW'),
        enabled: !!planUUID,
    });

    const { data: partnerFwsRelated, error: partnerError } = useQuery({
        queryKey: ['partnerFwsRelated', planUUID],
        queryFn: () => fetchAndSetFrameworks('partner', 'B2B_PARTNERFW'),
        enabled: !!planUUID,
    });    
    
    useEffect(() => {
        if (genericError || iotError || verticalError || softwareError || partnerError) {
            const error = genericError || iotError || verticalError || softwareError || partnerError;
            console.error('Error fetching Fws related to plan:', error);
            showNotification(setNotification, t('Error fetching Fws related to plan: ') + error.message, 'error', 5000);
        }
    }, [genericError, iotError, verticalError, softwareError, partnerError]);

    return { genericFwsRelated, iotFwsRelated, verticalFwsRelated, softwareFwsRelated, partnerFwsRelated };
}

export default usePlansFrameworks;